import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  useTheme,
  alpha,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";
import ArrowBack from "@mui/icons-material/ArrowBack";
import AttachMoney from "@mui/icons-material/AttachMoney";
import Edit from "@mui/icons-material/Edit";
import ToggleOn from "@mui/icons-material/ToggleOn";
import ToggleOff from "@mui/icons-material/ToggleOff";
import {
  useGetBillingCycleDetail,
  useUpdateBillingCycle,
} from "../../hooks/api/useBillingCycle";
import type { BillingCycle } from "../../Graphql/types/billingCycle";
import {
  DURATION_UNITS,
  formatDurationUnitLabel,
  type DurationUnit,
} from "../../Graphql/types/billingCycle";
import { ROUTE_MEMBERSHIP } from "../../constants/routes";
import { responsiveHeading } from "../../styles/layoutStyles";

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString(undefined, { dateStyle: "medium" });
  } catch {
    return iso;
  }
}

function DetailItem({
  label,
  value,
  mono,
}: {
  label: string;
  value: string;
  mono?: boolean;
}) {
  return (
    <Box>
      <Typography
        variant="caption"
        color="text.secondary"
        display="block"
        sx={{ mb: 0.5 }}
      >
        {label}
      </Typography>
      <Typography
        variant="body1"
        sx={{
          fontFamily: mono ? "monospace" : undefined,
          fontSize: mono ? "0.875rem" : undefined,
        }}
      >
        {value || "—"}
      </Typography>
    </Box>
  );
}

const BillingCycleDetailView: React.FC = () => {
  const { billingCycleId } = useParams<{ billingCycleId: string }>();
  const navigate = useNavigate();
  const theme = useTheme();
  const [cycle, setCycle] = useState<BillingCycle | null>(null);
  const [editOpen, setEditOpen] = useState(false);
  const [confirmStatusOpen, setConfirmStatusOpen] = useState(false);
  const [pendingActive, setPendingActive] = useState<boolean | null>(null);

  const [editLabel, setEditLabel] = useState("");
  const [editDurationValue, setEditDurationValue] = useState(1);
  const [editDurationUnit, setEditDurationUnit] =
    useState<DurationUnit>("MONTH");

  const getBillingCycleDetail = useGetBillingCycleDetail();
  const updateBillingCycle = useUpdateBillingCycle();

  useEffect(() => {
    if (!billingCycleId) return;
    const id = decodeURIComponent(billingCycleId);
    getBillingCycleDetail
      .mutateAsync({ billingCycleId: id })
      .then((res) => {
        const c = res.getBillingCycle?.billingCycle ?? null;
        setCycle(c);
        if (c) {
          setEditLabel(c.label);
          setEditDurationValue(c.durationValue);
          setEditDurationUnit(c.durationUnit);
        }
      })
      .catch(() => {});
  }, [billingCycleId]);

  const openEdit = () => {
    if (cycle) {
      setEditLabel(cycle.label);
      setEditDurationValue(cycle.durationValue);
      setEditDurationUnit(cycle.durationUnit);
      setEditOpen(true);
    }
  };

  const handleSaveEdit = () => {
    if (!billingCycleId) return;
    const id = decodeURIComponent(billingCycleId);
    updateBillingCycle
      .mutateAsync({
        billingCycleId: id,
        input: {
          label: editLabel,
          durationValue: editDurationValue,
          durationUnit: editDurationUnit,
        },
      })
      .then((res) => {
        const updated = res.updateBillingCycle?.billingCycle;
        if (updated) setCycle(updated);
        setEditOpen(false);
      })
      .catch(() => {});
  };

  const pendingStatus = pendingActive;

  const openConfirmStatus = () => {
    if (!cycle) return;
    setPendingActive(!cycle.isActive);
    setConfirmStatusOpen(true);
  };

  const handleConfirmStatus = () => {
    if (!billingCycleId || pendingStatus == null) return;
    const id = decodeURIComponent(billingCycleId);
    updateBillingCycle
      .mutateAsync({ billingCycleId: id, input: { isActive: pendingStatus } })
      .then((res) => {
        const updated = res.updateBillingCycle?.billingCycle;
        if (updated) setCycle(updated);
        setConfirmStatusOpen(false);
        setPendingActive(null);
      })
      .catch(() => {});
  };

  const handleCloseConfirmStatus = () => {
    setConfirmStatusOpen(false);
    setPendingActive(null);
  };

  if (!billingCycleId) {
    return (
      <Box sx={{ width: "100%" }}>
        <Typography color="text.secondary">
          Missing billing cycle ID.
        </Typography>
      </Box>
    );
  }

  if (!getBillingCycleDetail.isPending && !cycle) {
    return (
      <Box sx={{ width: "100%" }}>
        <Typography color="text.secondary">Billing cycle not found.</Typography>
        <Button
          startIcon={<ArrowBack />}
          sx={{ mt: 1 }}
          onClick={() => navigate(ROUTE_MEMBERSHIP)}
        >
          Back to membership
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ width: "100%" }}>
      <IconButton
        onClick={() => navigate(ROUTE_MEMBERSHIP)}
        sx={{ mb: 2, color: "text.secondary" }}
        aria-label="Back to membership"
      >
        <ArrowBack />
      </IconButton>

      {cycle && (
        <>
          <Paper
            elevation={0}
            sx={{
              p: 3,
              mb: 3,
              borderRadius: 3,
              border: "1px solid",
              borderColor: "divider",
              background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.04)} 0%, ${theme.palette.background.paper} 100%)`,
              boxShadow: "0 2px 12px rgba(0, 0, 0, 0.04)",
            }}
          >
            <Box
              display="flex"
              alignItems="center"
              gap={2}
              flexWrap="wrap"
              justifyContent="space-between"
            >
              <Box
                display="flex"
                alignItems="center"
                gap={2}
                minWidth={0}
                flex={1}
              >
                <Box
                  sx={{
                    width: 56,
                    height: 56,
                    borderRadius: 2,
                    bgcolor: "primary.main",
                    color: "white",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <AttachMoney sx={{ fontSize: 32 }} />
                </Box>
                <Box minWidth={0}>
                  <Typography
                    variant="h5"
                    sx={{ ...responsiveHeading, mb: 0.5 }}
                  >
                    {cycle.label}
                  </Typography>
                  <Chip
                    label={cycle.code}
                    size="small"
                    sx={{ fontFamily: "monospace", fontWeight: 600 }}
                  />
                  <Chip
                    label={cycle.isActive ? "Active" : "Inactive"}
                    size="small"
                    color={cycle.isActive ? "success" : "default"}
                    sx={{ ml: 1, fontWeight: 600, textTransform: "uppercase" }}
                  />
                </Box>
              </Box>

              <Box
                display="flex"
                gap={1.5}
                flexWrap="wrap"
                justifyContent="flex-end"
                sx={{ flexShrink: 0 }}
              >
                <Button
                  variant="contained"
                  size="medium"
                  color={cycle.isActive ? "warning" : "primary"}
                  startIcon={cycle.isActive ? <ToggleOff /> : <ToggleOn />}
                  onClick={openConfirmStatus}
                  disabled={updateBillingCycle.isPending}
                >
                  {cycle.isActive ? "Deactivate" : "Activate"}
                </Button>
                <Button
                  variant="outlined"
                  size="medium"
                  startIcon={<Edit />}
                  onClick={openEdit}
                >
                  Edit
                </Button>
              </Box>
            </Box>
          </Paper>

          <Paper
            elevation={0}
            sx={{
              p: 3,
              borderRadius: 3,
              border: "1px solid",
              borderColor: "divider",
              boxShadow: "0 2px 12px rgba(0, 0, 0, 0.06)",
            }}
          >
            <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
              Billing cycle details
            </Typography>
            <Box
              display="grid"
              gridTemplateColumns={{ xs: "1fr", sm: "1fr 1fr" }}
              gap={3}
            >
              <DetailItem
                label="Billing cycle ID"
                value={cycle.billingCycleId}
                mono
              />
              <DetailItem label="Code" value={cycle.code} mono />
              <DetailItem label="Label" value={cycle.label} />
              <DetailItem
                label="Duration"
                value={`${cycle.durationValue} ${formatDurationUnitLabel(cycle.durationUnit).toLowerCase()}`}
              />
              <DetailItem label="Created" value={formatDate(cycle.createdAt)} />
              <DetailItem label="Updated" value={formatDate(cycle.updatedAt)} />
            </Box>
          </Paper>
        </>
      )}

      <Dialog
        open={editOpen}
        onClose={() => setEditOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3, boxShadow: "0 8px 32px rgba(0,0,0,0.12)" },
        }}
      >
        <DialogTitle sx={{ fontWeight: 600 }}>Edit billing cycle</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} sx={{ pt: 1 }}>
            <TextField
              label="Label"
              value={editLabel}
              onChange={(e) => setEditLabel(e.target.value)}
              fullWidth
              size="small"
            />
            <TextField
              label="Duration value"
              type="number"
              inputProps={{ min: 1 }}
              value={editDurationValue}
              onChange={(e) =>
                setEditDurationValue(parseInt(e.target.value, 10) || 1)
              }
              fullWidth
              size="small"
            />
            <FormControl fullWidth size="small">
              <InputLabel>Duration unit</InputLabel>
              <Select
                value={editDurationUnit}
                label="Duration unit"
                onChange={(e) =>
                  setEditDurationUnit(e.target.value as DurationUnit)
                }
              >
                {DURATION_UNITS.map((u) => (
                  <MenuItem key={u} value={u}>
                    {formatDurationUnitLabel(u)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setEditOpen(false)} color="inherit">
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveEdit}
            disabled={updateBillingCycle.isPending}
          >
            {updateBillingCycle.isPending ? "Saving…" : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={confirmStatusOpen}
        onClose={handleCloseConfirmStatus}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3, boxShadow: "0 8px 32px rgba(0,0,0,0.12)" },
        }}
      >
        <DialogTitle sx={{ fontWeight: 600 }}>
          {pendingStatus
            ? "Activate billing cycle?"
            : "Deactivate billing cycle?"}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            {cycle &&
              (pendingStatus
                ? `Are you sure you want to activate "${cycle.label}"?`
                : `Are you sure you want to deactivate "${cycle.label}"?`)}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCloseConfirmStatus} color="inherit">
            Cancel
          </Button>
          <Button
            variant="contained"
            color={pendingStatus ? "primary" : "warning"}
            onClick={handleConfirmStatus}
            disabled={updateBillingCycle.isPending}
          >
            {updateBillingCycle.isPending
              ? "Updating…"
              : pendingStatus
                ? "Activate"
                : "Deactivate"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BillingCycleDetailView;
